<template lang="html">
  <v-my-card
    v-if="parameters.length > 0"
    class="device"
  >
    <v-container
      class="device-container"
    >
      <v-layout
        row
        wrap
        align-center
        pa-2
        class="device-header"
      >
        <v-flex
          grow
          pa-0
        >
          <v-card-title
            class="device-title pl-3"
          >
            <h4 class="headline">
              {{ $t("general.pump.name") }} {{ collectionId }}
            </h4>
          </v-card-title>
        </v-flex>
        <v-flex
          shrink
          pa-0
        />
      </v-layout>

      <v-layout>
        <v-flex pa-0>
          <v-data-table
            :items="parameters"
            :locale="$i18n.locale"
            :total-items="parameters.length"
            class="device-table"
            hide-actions
            hide-headers
          >
            <template v-slot:items="props">
              <tr
                v-if="values[props.item.key] !== undefined"
                class="tr"
              >
                <td
                  :class="{ 'overlay-active': dialogOpened }"
                  class="text-xs-left unit"
                >
                  {{ props.item.label }}
                </td>
                <td
                  :class="{ 'overlay-active': dialogOpened }"
                  class="text-xs-left value icon-container"
                >
                  {{ `${format(values[props.item.key], props.item.type, props.item.formatting)} ${props.item.unit}` }}
                  <button
                    v-if="props.item.editable"
                    class="edit-icon"
                    @click.prevent="toggleDialog(props.item)"
                  >
                    <img
                      src="@/assets/images/pen-icon.svg"
                      alt="Edit"
                    >
                  </button>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>

      <div
        :class="{ active: dialogOpened }"
        class="overlay"
        @click.self="toggleDialog"
      >
        <v-my-card
          class="overlay-card pa-3"
          :flat="false"
        >
          <span>
            {{ $t('general.device.dialog.header_pre') }}
            {{ editParameter ? editParameter.label : "" }}
            {{ $t('general.device.dialog.header_post') }}
          </span>
          <form
            action="#"
            class="pt-2"
          >
            <div class="form-container">
              <select
                v-if="editParameter && editParameter.type === 'bool'"
                :selected="values[editParameter.key]"
                @change="updateValue = parseInt($event.target.value, 10)"
              >
                <option
                  v-for="option in boolOptions"
                  :key="option.value"
                  :value="option.value"
                  :selected="values[editParameter.key] === option.value"
                >
                  {{ option.text }}
                </option>
              </select>
              <input
                v-else
                :value="editParameter ? values[editParameter.key] : 0"
                type="text"
                name="text"
                placeholder="12.1"
                @input="updateValue = $event.target.value"
              >
              <div>
                <button
                  @click.prevent="sendCommand()"
                >
                  <img src="@/assets/images/correct.png">
                </button>
                <button
                  @click.prevent="toggleDialog(null)"
                >
                  <img src="@/assets/images/wrong.png">
                </button>
              </div>
            </div>
          </form>
        </v-my-card>
      </div>
    </v-container>
  </v-my-card>
</template>

<script>
import gql from 'graphql-tag';
import VMyCard from '@/components/Card.vue';
import sendCommandMutation from '@/queries/sendCommand';

function orderSort(a, b) {
  if (a.sortOrder > b.sortOrder) return 1;
  if (b.sortOrder > a.sortOrder) return -1;
  return 0;
}

const GET_LASTEST_VALUES_QUERY = gql`
  query getLatestValues(
    $key: String!
    $collectionId: String!
    $host: String!
    $topic: String!
  ) {
    getLatestValues(
      key: $key
      host: $host
      collectionId: $collectionId
      topic: $topic
    ) {
      time
      value
    }
  }
`;

export default {
  components: {
    VMyCard,
  },

  props: {
    type: {
      default: 'CO2',
      type: String,
    },
    collectionId: {
      default: '1',
      type: String,
    },
    machineKey: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      dialogOpened: false,
      editParameter: null,
      boolOptions: [{
        value: 0,
        text: 'Falsch',
      },
      {
        value: 1,
        text: 'Wahr',
      }],
      updateValue: null,
      error: null,
      parameters: [],
      values: {},
    };
  },
  apollo: {
    parameters: {
      query: gql`
        query GetPumpSettings (
          $deviceId: uuid!
          $collectionId: String!
        ) {
          device_pump_settings (
            where: {
              device: {
                id: {
                  _eq: $deviceId
                }
              }
              _and: {
                collectionId: {
                  _eq: $collectionId
                }
              }
            }
          ){
            key
            label
            type
            unit
            visible
            editable
            formatting
            sortOrder
          }
        }
      `,
      variables() {
        return {
          deviceId: this.$route.params.id,
          collectionId: this.collectionId,
        };
      },
      update: data => data.device_pump_settings.sort(orderSort),
    },
  },
  computed: {
    queryVariables() {
      return {
        host: 'gobblespit-oauth-pierre',
        collectionId: this.collectionId,
        topic: this.machineKey,
      };
    },
  },
  watch: {
    async parameters(newParameters) {
      try {
        await Promise.all(
          newParameters.map(async (parameter) => {
            const value = await this.$apollo.query({
              query: GET_LASTEST_VALUES_QUERY,
              variables: {
                key: `Values_${parameter.key}`,
                ...this.queryVariables,
              },
            });

            if (value.data.getLatestValues.length === 0) {
              return;
            }

            this.values = {
              ...this.values,
              [parameter.key]: value.data.getLatestValues[0].value,
            };
          }),
        );
      } catch (error) {
        this.error = error;
      }
    },
  },
  methods: {
    format(value, type, formatString) {
      if (type === 'text') {
        const splitString = formatString.split('.');
        const digits = splitString.length > 1 ?
          splitString[1].length :
          0;
        const factor = 10 ** digits;
        return (Math.round(value * factor) / factor).toFixed(digits);
      } else if (type === 'bool') {
        if (value === 1) {
          return 'Wahr';
        } else if (value === 0) {
          return 'Falsch';
        }
      }
      return 'Unbekannt';
    },
    toggleDialog(parameter) {
      this.editParameter = parameter;
      this.dialogOpened = !this.dialogOpened;
    },
    sendCommand() {
      // actually nothing has been set
      if (this.updateValue === null) return;

      const commandNamePrefix = this.editParameter.key;
      const pumpNumber = this.collectionId;

      // Map receive name to send name
      const commandMapping = {
        Status: 'OnOff',
        Stellwert: 'StellwertPumpe',
        Betriebsmodus: 'Betriebsmodus',
      };

      if (!Object.keys(commandMapping).find(key => key === commandNamePrefix)) {
        throw new Error('There is no registered command for this parameter key');
      }

      const commandName = `${commandMapping[commandNamePrefix]}${pumpNumber}`;

      let value;
      if (this.editParameter.type === 'bool') {
        value = [!!this.updateValue];
      } else {
        value = [parseFloat(this.updateValue)];
      }

      this.$apollo
        .mutate({
          mutation: sendCommandMutation,
          variables: {
            deviceId: this.machineKey,
            name: commandName,
            parameters: JSON.stringify(value),
          },
        })
        .then(async () => {
          this.reflectUpdatedValue(this.editParameter.key, this.updateValue);
          this.$root.vtoast.show({
            message: `${this.$t('general.device.dialog.commandReceived')}`,
            color: 'success',
          });
          this.toggleDialog();
        })
        .catch((error) => {
          // Error
          this.error = error;
        });
    },
    reflectUpdatedValue(key, value) {
      this.values = {
        ...this.values,
        [key]: value,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.icon-container {
  position: relative;
}
.edit-icon {
  height: 36px;
  position: absolute;
  right: 3px;
  top: 8px;
  width: 36px;

  img {
    max-width: 100%;
    max-height: 100%;
  }
}

.overlay {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  &.active {
    visibility: visible;
  }

  .overlay-card {
    transition: none !important;
    button {
      height: 100%;

      img {
        height: 26px;
        margin: 5px 5px 0;
      }
    }

    .form-container {
      display: flex;
      justify-content: space-between;
    }
  }

  input {
    max-width: 70%;
    border-bottom: 2px solid;
  }

  select {
    border-bottom: 2px solid;
  }
}
</style>
